import { useState } from 'react'
import { useMutation } from 'convex/react'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'
import { X, Trash2, Loader2, AlertTriangle } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'

interface RemoveDeckModalProps {
  deckId: Id<'flashcardDecks'>
  deckName: string
  flashcardCount: number
  onClose: () => void
}

export function RemoveDeckModal({
  deckId,
  deckName,
  flashcardCount,
  onClose,
}: RemoveDeckModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmationText, setConfirmationText] = useState('')

  const navigate = useNavigate()
  const removeDeck = useMutation(api.flashcards.removeDeck)

  const isConfirmed = confirmationText.toLowerCase() === 'delete'

  const handleRemove = async () => {
    if (!isConfirmed) return

    setIsLoading(true)
    setError(null)

    try {
      await removeDeck({ deckId })
      // Navigate back to decks list after successful deletion
      navigate({ to: '/app/decks' })
    } catch (err) {
      console.error('Failed to remove deck:', err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove deck'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="bg-black border-4 border-red-500 shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-red-500">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <h2 className="text-xl font-bold text-red-500 font-mono tracking-wider">
              [WARNING: REMOVE DECK]
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-trail-tan hover:text-sky-blue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 border-2 border-red-500 bg-red-500/10">
              <p className="text-red-500 font-mono text-sm">{error}</p>
            </div>
          )}

          {/* Warning Box */}
          <div className="mb-6 p-4 border-2 border-red-500 bg-red-500/10">
            <div className="text-red-500 font-mono text-sm font-bold mb-3">
              ⚠️ THIS ACTION CANNOT BE UNDONE
            </div>
            <ul className="space-y-2 text-trail-tan font-mono text-sm">
              <li className="flex items-start gap-2">
                <span className="text-red-500">●</span>
                <span>
                  Permanently delete <span className="text-white font-bold">"{deckName}"</span>
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">●</span>
                <span>
                  Delete all <span className="text-white font-bold">{flashcardCount}</span> flashcard
                  {flashcardCount !== 1 ? 's' : ''} in this deck
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">●</span>
                <span>Lose all progress and review history</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500">●</span>
                <span>Cannot recover data after deletion</span>
              </li>
            </ul>
          </div>

          {/* Confirmation Input */}
          <div className="mb-6">
            <label className="block text-trail-tan font-mono text-sm mb-2">
              Type <span className="text-white font-bold">"delete"</span> to confirm:
            </label>
            <input
              type="text"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              disabled={isLoading}
              placeholder="delete"
              className="w-full px-4 py-3 bg-black border-2 border-trail-tan text-white font-mono focus:border-sky-blue focus:outline-none disabled:opacity-50"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleRemove}
              disabled={isLoading || !isConfirmed}
              className="flex-1 px-6 py-3 bg-red-500 text-white font-bold font-mono border-4 border-red-500 hover:bg-black hover:text-red-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>REMOVING...</span>
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  <span>REMOVE DECK PERMANENTLY</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-6 py-3 border-4 border-sky-blue text-sky-blue font-bold font-mono hover:bg-sky-blue hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
